import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, LogIn, LogOut, Eye, Edit2, Trash2, FileText, UserPlus,
  AlertTriangle, Clock, Filter, RefreshCw, Download, ChevronDown,
  Monitor, Smartphone, Globe
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { AuditService, AuditEntry, AuditAction, AuditResourceType } from '../../../services/firebase/AuditService';

type ViewMode = 'sign-ins' | 'all-activity' | 'failed-logins';
type TimeFilter = '24h' | '7d' | '30d' | '90d' | 'all';

const ACTION_LABELS: Record<AuditAction, string> = {
  view: 'Viewed',
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  export: 'Exported',
  print: 'Printed',
  login: 'Signed in',
  logout: 'Signed out',
  login_failed: 'Failed login attempt',
  inquiry_created: 'Inquiry created',
  invite_created: 'Invite sent',
  invite_accepted: 'Invite accepted',
};

const RESOURCE_LABELS: Record<AuditResourceType, string> = {
  patient: 'Patient Record',
  appointment: 'Appointment',
  note: 'Clinical Note',
  psychotherapy_note: 'Psychotherapy Note',
  form: 'Form',
  recording: 'Recording',
  session: 'Session',
  auth: 'Authentication',
  inquiry: 'Inquiry',
  invite: 'Team Invite',
  eligibility: 'Insurance Verification',
};

const ACTION_ICONS: Record<string, React.FC<any>> = {
  login: LogIn,
  logout: LogOut,
  view: Eye,
  create: FileText,
  update: Edit2,
  delete: Trash2,
  login_failed: AlertTriangle,
  invite_created: UserPlus,
  invite_accepted: UserPlus,
};

const ACTION_COLORS: Record<string, string> = {
  login: '#10b981',
  logout: '#64748b',
  view: '#3b82f6',
  create: '#8b5cf6',
  update: '#f59e0b',
  delete: '#ef4444',
  login_failed: '#ef4444',
  export: '#06b6d4',
  print: '#06b6d4',
  invite_created: '#8b5cf6',
  invite_accepted: '#10b981',
};

function getDeviceIcon(userAgent?: string) {
  if (!userAgent) return Globe;
  const ua = userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) return Smartphone;
  return Monitor;
}

function getBrowserName(userAgent?: string): string {
  if (!userAgent) return 'Unknown';
  const ua = userAgent.toLowerCase();
  if (ua.includes('chrome') && !ua.includes('edg')) return 'Chrome';
  if (ua.includes('firefox')) return 'Firefox';
  if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari';
  if (ua.includes('edg')) return 'Edge';
  return 'Browser';
}

function getDeviceType(userAgent?: string): string {
  if (!userAgent) return 'Unknown device';
  const ua = userAgent.toLowerCase();
  if (ua.includes('iphone')) return 'iPhone';
  if (ua.includes('ipad')) return 'iPad';
  if (ua.includes('android') && ua.includes('mobile')) return 'Android Phone';
  if (ua.includes('android')) return 'Android Tablet';
  if (ua.includes('macintosh') || ua.includes('mac os')) return 'Mac';
  if (ua.includes('windows')) return 'Windows PC';
  if (ua.includes('linux')) return 'Linux';
  return 'Desktop';
}

function formatTimestamp(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatFullTimestamp(date: Date): string {
  return date.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

const AccountActivity: React.FC = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('sign-ins');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('30d');
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);

  const fetchActivity = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const allEntries = await AuditService.getUserAuditTrail(user.uid, 500);

      // Apply time filter
      const now = new Date();
      const cutoff = new Date();
      switch (timeFilter) {
        case '24h': cutoff.setHours(cutoff.getHours() - 24); break;
        case '7d': cutoff.setDate(cutoff.getDate() - 7); break;
        case '30d': cutoff.setDate(cutoff.getDate() - 30); break;
        case '90d': cutoff.setDate(cutoff.getDate() - 90); break;
        case 'all': cutoff.setFullYear(2020); break;
      }

      let filtered = allEntries.filter(e => {
        const ts = e.timestamp instanceof Date ? e.timestamp : new Date(e.timestamp);
        return ts >= cutoff;
      });

      // Apply view mode filter
      if (viewMode === 'sign-ins') {
        filtered = filtered.filter(e => e.action === 'login' || e.action === 'logout');
      } else if (viewMode === 'failed-logins') {
        filtered = filtered.filter(e => e.action === 'login_failed');
      }

      setEntries(filtered);
    } catch (err) {
      console.error('Failed to fetch account activity:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, viewMode, timeFilter]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  // Group entries by date
  const groupedEntries: Record<string, AuditEntry[]> = {};
  entries.forEach(entry => {
    const ts = entry.timestamp instanceof Date ? entry.timestamp : new Date(entry.timestamp);
    const dateKey = ts.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    if (!groupedEntries[dateKey]) groupedEntries[dateKey] = [];
    groupedEntries[dateKey].push(entry);
  });

  // Stats
  const signInCount = entries.filter(e => e.action === 'login').length;
  const lastLogin = entries.find(e => e.action === 'login');
  const failedCount = entries.filter(e => e.action === 'login_failed').length;

  const viewModes: { id: ViewMode; label: string; count?: number }[] = [
    { id: 'sign-ins', label: 'Sign-in Events', count: entries.filter(e => e.action === 'login' || e.action === 'logout').length },
    { id: 'all-activity', label: 'All Activity' },
    { id: 'failed-logins', label: 'Failed Logins', count: failedCount },
  ];

  const timeFilters: { id: TimeFilter; label: string }[] = [
    { id: '24h', label: 'Last 24 hours' },
    { id: '7d', label: 'Last 7 days' },
    { id: '30d', label: 'Last 30 days' },
    { id: '90d', label: 'Last 90 days' },
    { id: 'all', label: 'All time' },
  ];

  return (
    <div style={{ padding: '32px', maxWidth: '900px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <Shield size={28} color="#6CA6C1" />
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: 0 }}>
            Account Activity
          </h1>
        </div>
        <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
          View sign-in history, account events, and security audit log
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {/* Last Sign-in */}
        <div style={{
          backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '8px', padding: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <LogIn size={16} color="#10b981" />
            <span style={{ fontSize: '12px', color: '#87CEEB', textTransform: 'uppercase', fontWeight: 600 }}>Last Sign-in</span>
          </div>
          <div style={{ fontSize: '16px', color: '#FFFFFF', fontWeight: 600 }}>
            {lastLogin ? formatTimestamp(lastLogin.timestamp instanceof Date ? lastLogin.timestamp : new Date(lastLogin.timestamp)) : 'No sign-ins'}
          </div>
          {lastLogin && (
            <div style={{ fontSize: '12px', color: '#87CEEB', marginTop: '4px' }}>
              {getBrowserName(lastLogin.userAgent)} on {getDeviceType(lastLogin.userAgent)}
            </div>
          )}
        </div>

        {/* Sign-in Count */}
        <div style={{
          backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '8px', padding: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Clock size={16} color="#3b82f6" />
            <span style={{ fontSize: '12px', color: '#87CEEB', textTransform: 'uppercase', fontWeight: 600 }}>
              Sign-ins ({timeFilter === 'all' ? 'All Time' : `Last ${timeFilter}`})
            </span>
          </div>
          <div style={{ fontSize: '24px', color: '#FFFFFF', fontWeight: 700 }}>
            {viewMode === 'sign-ins' ? signInCount : entries.length}
          </div>
        </div>

        {/* Failed Logins */}
        <div style={{
          backgroundColor: failedCount > 0 ? '#3b1318' : '#113F67',
          border: `1px solid ${failedCount > 0 ? '#ef4444' : '#6CA6C1'}`,
          borderRadius: '8px', padding: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <AlertTriangle size={16} color={failedCount > 0 ? '#ef4444' : '#64748b'} />
            <span style={{ fontSize: '12px', color: '#87CEEB', textTransform: 'uppercase', fontWeight: 600 }}>Failed Logins</span>
          </div>
          <div style={{ fontSize: '24px', color: failedCount > 0 ? '#ef4444' : '#FFFFFF', fontWeight: 700 }}>
            {failedCount}
          </div>
          {failedCount > 0 && (
            <div style={{ fontSize: '12px', color: '#f87171', marginTop: '4px' }}>
              Review for suspicious activity
            </div>
          )}
        </div>
      </div>

      {/* Controls Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '16px', flexWrap: 'wrap', gap: '12px'
      }}>
        {/* View Mode Tabs */}
        <div style={{ display: 'flex', gap: '4px', backgroundColor: '#0A2540', borderRadius: '8px', padding: '4px' }}>
          {viewModes.map(mode => (
            <button
              key={mode.id}
              onClick={() => setViewMode(mode.id)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: viewMode === mode.id ? '#28527A' : 'transparent',
                color: viewMode === mode.id ? '#FFFFFF' : '#87CEEB',
                fontSize: '13px',
                fontWeight: viewMode === mode.id ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {mode.label}
            </button>
          ))}
        </div>

        {/* Time Filter + Refresh */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ position: 'relative' }}>
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
              style={{
                padding: '8px 32px 8px 12px',
                backgroundColor: '#113F67',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '13px',
                cursor: 'pointer',
                appearance: 'none',
              }}
            >
              {timeFilters.map(tf => (
                <option key={tf.id} value={tf.id}>{tf.label}</option>
              ))}
            </select>
            <ChevronDown size={14} color="#87CEEB" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          </div>

          <button
            onClick={fetchActivity}
            disabled={loading}
            style={{
              padding: '8px 12px',
              backgroundColor: '#113F67',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              color: '#FFFFFF',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
            }}
          >
            <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
      </div>

      {/* Activity List */}
      <div style={{
        backgroundColor: '#113F67',
        border: '1px solid #6CA6C1',
        borderRadius: '8px',
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <RefreshCw size={24} color="#6CA6C1" style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ color: '#87CEEB', marginTop: '12px' }}>Loading activity...</p>
          </div>
        ) : entries.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <Shield size={32} color="#6CA6C1" style={{ opacity: 0.5 }} />
            <p style={{ color: '#87CEEB', marginTop: '12px' }}>No activity found for this time period</p>
          </div>
        ) : (
          Object.entries(groupedEntries).map(([dateLabel, dateEntries]) => (
            <div key={dateLabel}>
              {/* Date Header */}
              <div style={{
                padding: '12px 20px',
                backgroundColor: '#0A2540',
                borderBottom: '1px solid #1e3a5f',
                fontSize: '13px',
                fontWeight: 600,
                color: '#F7E9A0',
              }}>
                {dateLabel}
              </div>

              {/* Entries */}
              {dateEntries.map((entry) => {
                const ts = entry.timestamp instanceof Date ? entry.timestamp : new Date(entry.timestamp);
                const IconComponent = ACTION_ICONS[entry.action] || Eye;
                const color = ACTION_COLORS[entry.action] || '#6CA6C1';
                const DeviceIcon = getDeviceIcon(entry.userAgent);
                const isExpanded = expandedEntry === entry.id;

                return (
                  <div key={entry.id}>
                    <button
                      onClick={() => setExpandedEntry(isExpanded ? null : (entry.id || null))}
                      style={{
                        width: '100%',
                        padding: '16px 20px',
                        border: 'none',
                        borderBottom: '1px solid #1e3a5f',
                        backgroundColor: isExpanded ? '#1a3a5c' : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        textAlign: 'left',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#153050'; }}
                      onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {/* Action Icon */}
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        backgroundColor: `${color}20`, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <IconComponent size={18} color={color} />
                      </div>

                      {/* Action Description */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', color: '#FFFFFF', fontWeight: 500 }}>
                          {ACTION_LABELS[entry.action] || entry.action}
                          {entry.resourceType !== 'auth' && (
                            <span style={{ color: '#87CEEB', fontWeight: 400 }}>
                              {' '}{RESOURCE_LABELS[entry.resourceType] || entry.resourceType}
                            </span>
                          )}
                        </div>
                        {entry.details && (
                          <div style={{ fontSize: '12px', color: '#87CEEB', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.details}
                          </div>
                        )}
                      </div>

                      {/* Device + Time */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                        <DeviceIcon size={14} color="#64748b" />
                        <span style={{ fontSize: '13px', color: '#87CEEB', whiteSpace: 'nowrap' }}>
                          {ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </span>
                        <ChevronDown
                          size={14}
                          color="#64748b"
                          style={{
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </div>
                    </button>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div style={{
                        padding: '16px 20px 16px 72px',
                        backgroundColor: '#0f2d47',
                        borderBottom: '1px solid #1e3a5f',
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', fontSize: '13px' }}>
                          <span style={{ color: '#64748b' }}>Timestamp</span>
                          <span style={{ color: '#FFFFFF' }}>{formatFullTimestamp(ts)}</span>

                          <span style={{ color: '#64748b' }}>Action</span>
                          <span style={{ color: '#FFFFFF' }}>{ACTION_LABELS[entry.action] || entry.action}</span>

                          <span style={{ color: '#64748b' }}>Resource Type</span>
                          <span style={{ color: '#FFFFFF' }}>{RESOURCE_LABELS[entry.resourceType] || entry.resourceType}</span>

                          {entry.resourceId && entry.resourceType !== 'auth' && (
                            <>
                              <span style={{ color: '#64748b' }}>Resource ID</span>
                              <span style={{ color: '#FFFFFF', fontFamily: 'monospace', fontSize: '12px' }}>{entry.resourceId}</span>
                            </>
                          )}

                          {entry.details && (
                            <>
                              <span style={{ color: '#64748b' }}>Details</span>
                              <span style={{ color: '#FFFFFF' }}>{entry.details}</span>
                            </>
                          )}

                          <span style={{ color: '#64748b' }}>Browser</span>
                          <span style={{ color: '#FFFFFF' }}>{getBrowserName(entry.userAgent)}</span>

                          <span style={{ color: '#64748b' }}>Device</span>
                          <span style={{ color: '#FFFFFF' }}>{getDeviceType(entry.userAgent)}</span>

                          {entry.ipAddress && (
                            <>
                              <span style={{ color: '#64748b' }}>IP Address</span>
                              <span style={{ color: '#FFFFFF', fontFamily: 'monospace', fontSize: '12px' }}>{entry.ipAddress}</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* HIPAA Notice */}
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        backgroundColor: '#0A2540',
        borderRadius: '8px',
        border: '1px solid #1e3a5f',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <Shield size={14} color="#64748b" />
        <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
          Account activity is logged per HIPAA requirements. Audit records are immutable and cannot be modified or deleted.
          If you notice suspicious activity, contact your system administrator immediately.
        </p>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default AccountActivity;
